import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { SalesForceService } from 'src/app/core/services/sales-force.service';

@Injectable()
export class SfAuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(private readonly salesForceService: SalesForceService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only handle Salesforce requests
    if (!req.url.includes('my.salesforce.com')) {
      return next.handle(req);
    }

    return this.getValidToken().pipe(
      switchMap((token) => this.handleRequestWithToken(req, next, token))
    );
  }

  private getValidToken(): Observable<string> {
    const token = this.salesForceService.getAccessToken();
    return token
      ? of(token)
      : this.salesForceService
          .requestAccessToken()
          .pipe(
            switchMap((res: any) =>
              of(res?.access_token ?? this.salesForceService.getAccessToken())
            )
          );
  }

  private handleRequestWithToken(
    req: HttpRequest<any>,
    next: HttpHandler,
    token: string
  ): Observable<HttpEvent<any>> {
    const authReq = req.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return next
      .handle(authReq)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(req, next, error)
        )
      );
  }

  private handleError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    // Retry if 401 or INVALID_SESSION_ID and not already refreshing
    if (
      (error.status === 401 ||
        (Array.isArray(error.error) &&
          error.error[0]?.errorCode === 'INVALID_SESSION_ID')) &&
      !this.isRefreshing
    ) {
      this.isRefreshing = true;
      return this.salesForceService.requestAccessToken().pipe(
        switchMap((res: any) => {
          const newToken =
            res?.access_token || this.salesForceService.getAccessToken();
          this.isRefreshing = false;
          return this.handleRequestWithToken(req, next, newToken);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          return throwError(() => err);
        })
      );
    }

    return throwError(() => error);
  }
}
